import type { VonqPostingRequirementField } from 'api-clients/monolith';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { FormTagInput } from '@/components/ui/tag-input';
import { Textarea } from '@/components/ui/textarea';
import { TiptapEditor } from '@/components/ui/tiptap-editor';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { stripVonq } from '@/utils/strip-vonq';

import { VonqAutocompleteInput } from './autocomplete-input';
import type { PostingRequirementsDef } from './requirements-key';

interface PostingRequirementInputProps {
  openingId: string;
  def: PostingRequirementsDef;
  field: VonqPostingRequirementField;
}

export function PostingRequirementInput({
  openingId,
  def,
  field,
}: PostingRequirementInputProps) {
  const { hookForm } = useJobPostingContext();
  const {
    register,
    control,
    formState: { errors },
  } = hookForm;
  const intl = useIntl();

  const fieldPath =
    `jobDetails.${openingId}.vonqPostingRequirements.${def.key}.${field.name}` as const;

  const errorMessage =
    stripVonq(
      errors?.jobDetails?.[openingId]?.vonqPostingRequirements?.[def.key]?.[
        field.name
      ]?.message,
    ) || undefined;

  const displayLabel = stripVonq(field.label);

  // Derive attributes from field rules
  const rules = field.rules ?? [];
  const hasIntRule = rules.some(r => r.rule === 'int');
  const hasFloatRule = rules.some(r => r.rule === 'float');
  const hasEmailRule = rules.some(r => r.rule === 'email');
  const hasUrlRule = rules.some(r => r.rule === 'url');

  const maxLengthRule = rules.find(r => r.rule === 'maxlength');
  const maxLength = maxLengthRule?.data
    ? parseInt(maxLengthRule.data, 10)
    : undefined;

  const minLengthRule = rules.find(r => r.rule === 'minlength');
  const minLength = minLengthRule?.data
    ? parseInt(minLengthRule.data, 10)
    : undefined;

  // Determine input type for TEXT/TEXTEXPAND fields based on rules
  let inputType: React.HTMLInputTypeAttribute = 'text';
  if (hasIntRule) inputType = 'number';
  else if (hasEmailRule) inputType = 'email';
  else if (hasUrlRule) inputType = 'url';
  // float keeps type="text" — isNumber handles live filtering; browser number
  // inputs reject comma thousands-separators which VONQ's float format allows

  switch (field.type) {
    case 'TEXT':
      return (
        <Input
          label={displayLabel}
          required={field.required}
          error={errorMessage}
          isNumber={hasFloatRule && !hasIntRule}
          type={inputType}
          step={hasIntRule ? '1' : undefined}
          maxLength={maxLength}
          minLength={minLength}
          {...register(fieldPath)}
        />
      );

    case 'TEXTEXPAND':
      // Multi-value free text — each entry becomes a tag, submitted as an array.
      return (
        <FormTagInput
          name={fieldPath}
          control={control}
          label={displayLabel}
          required={field.required}
          error={errorMessage}
          maxLength={maxLength}
          placeholder={intl.formatMessage({
            defaultMessage: 'Add a tag',
            description:
              'Placeholder for a VONQ multi-value (TEXTEXPAND) posting requirement field',
          })}
          helperText={intl.formatMessage({
            defaultMessage: 'Hit enter to add your tag(s)',
            description:
              'Hint telling the user to press enter to commit each tag in a VONQ multi-value field',
          })}
        />
      );

    case 'TEXTAREA':
    case 'AREACOUNT':
      return (
        <Controller
          name={fieldPath}
          control={control}
          render={({ field: f }) => (
            <div className="flex flex-col gap-1">
              <label>
                <Typography variant="subtitle2">
                  {displayLabel}{' '}
                  {field.required && <span className="text-fx-rose">*</span>}
                </Typography>
              </label>
              <Textarea
                value={f.value as string}
                onChange={f.onChange}
                maxLength={maxLength}
                className="min-h-[80px]"
              />
              {field.type === 'AREACOUNT' && maxLength != null && (
                <Typography
                  variant="small1"
                  className="!text-fx-neutral-600 text-right"
                >
                  {String(f.value ?? '').length} / {maxLength}
                </Typography>
              )}
              {errorMessage && (
                <Typography variant="small1" className="!text-fx-rose">
                  {errorMessage}
                </Typography>
              )}
            </div>
          )}
        />
      );

    case 'HTMLAREA':
      return (
        <Controller
          name={fieldPath}
          control={control}
          render={({ field: f }) => (
            <div className="flex flex-col gap-1">
              <label>
                <Typography variant="subtitle2">
                  {displayLabel}{' '}
                  {field.required && <span className="text-fx-rose">*</span>}
                </Typography>
              </label>
              <div
                className={
                  errorMessage ? 'border-fx-rose rounded-lg border' : ''
                }
              >
                <TiptapEditor
                  content={f.value as string}
                  onChange={f.onChange}
                  preset="basic"
                />
              </div>
              {errorMessage && (
                <Typography variant="small1" className="!text-fx-rose">
                  {errorMessage}
                </Typography>
              )}
            </div>
          )}
        />
      );

    case 'DATE':
      return (
        <Controller
          name={fieldPath}
          control={control}
          render={({ field: f }) => (
            <FormDatePicker
              name={fieldPath}
              control={control}
              label={displayLabel}
              required={field.required}
              error={errorMessage}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select date',
                description:
                  'Placeholder for a VONQ posting requirement date picker',
              })}
              onChange={isoString => f.onChange(isoString.slice(0, 10))}
            />
          )}
        />
      );

    case 'SELECT':
    case 'HIER':
    case 'AUTOCOMPLETE':
      return (
        <VonqAutocompleteInput
          field={field}
          def={def}
          openingId={openingId}
          name={fieldPath}
          control={control}
          errorMessage={errorMessage}
        />
      );

    case 'MULTIPLE':
      if (field.autocomplete !== null) {
        return (
          <VonqAutocompleteInput
            field={field}
            def={def}
            openingId={openingId}
            name={fieldPath}
            control={control}
            errorMessage={errorMessage}
            isMultiSelect={true}
          />
        );
      }
      return (
        <FormSelect
          name={fieldPath}
          control={control}
          label={displayLabel}
          required={field.required}
          error={errorMessage}
          isMultiSelect={true}
          options={field.options.map(opt => ({
            value: opt.key,
            label: stripVonq(opt.label),
          }))}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select options',
            description:
              'Placeholder for a multi-select VONQ posting requirement dropdown',
          })}
        />
      );

    case 'STATISCH':
      return (
        <div className="flex flex-col gap-1">
          <Typography variant="subtitle2">{displayLabel}</Typography>
          <Typography variant="body1">
            {stripVonq(field.message) || '-'}
          </Typography>
        </div>
      );

    case 'QUESTIONNAIRE':
      // Not supported in v1 — render a descriptive placeholder
      return (
        <div className="flex flex-col gap-1">
          <Typography variant="subtitle2">{displayLabel}</Typography>
          <Typography variant="small1" className="!text-fx-neutral-500">
            {intl.formatMessage({
              defaultMessage: 'This field type is not yet supported.',
              description:
                'Placeholder shown for VONQ questionnaire field types that are not yet implemented',
            })}
          </Typography>
        </div>
      );

    default:
      return null;
  }
}
